/**
 * Modal dialog. Focus moves inside on open, is trapped there while it is up,
 * and returns to whatever opened it. Escape and a scrim click both close.
 */
import { el, append, type Child } from './dom';

const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export interface DialogHandle {
  root: HTMLElement;
  body: HTMLElement;
  foot: HTMLElement;
  close: () => void;
  /** Resolves when the dialog closes. */
  closed: Promise<void>;
}

export interface DialogOptions {
  title: string;
  description?: string;
  body?: Child[];
  /** Footer content; usually buttons. */
  foot?: Child[];
  wide?: boolean;
  /** Escape and scrim clicks are ignored when false. */
  dismissable?: boolean;
  onClose?: () => void;
}

/**
 * One id per dialog ever opened, because more than one can be in the document
 * at a time — a confirmation over the share dialog is two — and with a shared
 * id `aria-labelledby` names whichever the engine finds first, which is the one
 * underneath.
 */
let dialogSeq = 0;

export function openDialog(options: DialogOptions): DialogHandle {
  const previous = document.activeElement as HTMLElement | null;
  const dismissable = options.dismissable !== false;

  const titleId = `dlg-title-${++dialogSeq}`;
  const titleEl = el('h2', { class: 'dialog__title', id: titleId, text: options.title });
  const body = el('div', { class: 'dialog__body' });
  if (options.description) {
    body.appendChild(el('p', { class: 'muted', text: options.description }));
  }
  append(body, options.body ?? []);
  const foot = el('div', { class: 'dialog__foot' });
  append(foot, options.foot ?? []);

  const dialog = el(
    'div',
    {
      class: options.wide ? 'dialog dialog--wide' : 'dialog',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': titleId,
    },
    el('div', { class: 'dialog__head' }, titleEl),
    body,
    foot.childElementCount || options.foot?.length ? foot : null,
  );

  const scrim = el('div', { class: 'scrim' }, dialog);

  let closed = false;
  let resolveClosed: () => void = () => {};
  const closedPromise = new Promise<void>((resolve) => (resolveClosed = resolve));

  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKeyDown, true);
    scrim.remove();
    previous?.focus?.();
    options.onClose?.();
    resolveClosed();
  };

  function onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && dismissable) {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    // `hidden` is how this code hides controls, and unlike `offsetParent` it
    // reads the same in a real browser and in jsdom. The sort is belt and
    // braces: a selector list is not ordered by every engine.
    const items = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)]
      .filter((n) => !n.closest('[hidden]'))
      .sort((a, b) =>
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
      );
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  scrim.addEventListener('mousedown', (event) => {
    if (event.target === scrim && dismissable) close();
  });
  document.addEventListener('keydown', onKeyDown, true);
  document.body.appendChild(scrim);

  const initial =
    dialog.querySelector<HTMLElement>('[data-autofocus]') ??
    dialog.querySelector<HTMLElement>(FOCUSABLE) ??
    dialog;
  initial.focus({ preventScroll: true });

  return { root: dialog, body, foot, close, closed: closedPromise };
}

/**
 * A question with more than two answers — Save, Discard, Cancel.
 *
 * Dismissing it (Escape, the scrim, the Cancel button) resolves to `cancel`,
 * so "I did not mean to be asked" and "no" are the same answer and a caller
 * never has to tell them apart.
 */
export function choiceDialog<T extends string>(options: {
  title: string;
  description: string;
  /** In reading order; the last is the one focus lands on. */
  choices: { value: T; label: string; class?: string }[];
  cancel: T;
  cancelLabel?: string;
}): Promise<T> {
  return new Promise((resolve) => {
    let answer: T = options.cancel;
    // The buttons go in through `foot` rather than being appended afterwards,
    // so `openDialog` can see the `data-autofocus` when it decides where focus
    // lands.
    let close = (): void => {};
    const pick = (value: T): void => {
      answer = value;
      close();
    };
    const buttons = options.choices.map((choice, i) =>
      el('button', {
        class: choice.class ? `btn ${choice.class}` : 'btn',
        type: 'button',
        text: choice.label,
        'data-choice': choice.value,
        ...(i === options.choices.length - 1 ? { 'data-autofocus': '' } : {}),
        onClick: () => pick(choice.value),
      }),
    );
    const handle = openDialog({
      title: options.title,
      description: options.description,
      onClose: () => resolve(answer),
      foot: [
        el('button', {
          class: 'btn btn--quiet',
          type: 'button',
          text: options.cancelLabel ?? 'Cancel',
          'data-choice': options.cancel,
          onClick: () => pick(options.cancel),
        }),
        el('span', { class: 'grow' }),
        ...buttons,
      ],
    });
    close = handle.close;
  });
}

/** Yes/no question. Resolves true when the confirming button is pressed. */
export function confirmDialog(options: {
  title: string;
  description: string;
  confirmLabel: string;
  danger?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    let answer = false;
    // The buttons go in through `foot`, like `choiceDialog`'s: appending them
    // afterwards leaves `openDialog` choosing initial focus from an empty
    // dialog, and the `data-autofocus` below says nothing.
    let close = (): void => {};
    const cancel = el('button', {
      class: 'btn',
      type: 'button',
      text: 'Cancel',
      onClick: () => close(),
    });
    const confirm = el('button', {
      class: options.danger ? 'btn btn--danger' : 'btn btn--primary',
      type: 'button',
      text: options.confirmLabel,
      'data-autofocus': '',
      onClick: () => {
        answer = true;
        close();
      },
    });
    const handle = openDialog({
      title: options.title,
      description: options.description,
      onClose: () => resolve(answer),
      foot: [el('span', { class: 'grow' }), cancel, confirm],
    });
    close = handle.close;
  });
}
